// src/pages/qbo/qboJobs.ts
// Push and conversion runs execute in the agent as background jobs (the
// Cloudflare tunnel cuts a request at 100 s). `runJob` starts one, polls it
// and resolves with the final SyncResult; `onProgress` feeds the progress bar.

import qboApi from '../../services/qboApi';
import type { SyncResult } from './qboTypes';

export interface JobView {
  jobId: string;
  kind: string;
  status: 'running' | 'done' | 'failed' | 'cancelled';
  done: number;
  total: number;
  startedUtc: string;
  finishedUtc?: string | null;
  result?: SyncResult | null;
  error?: string | null;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export async function runJob(
  start: () => Promise<unknown>,
  onProgress: (j: JobView) => void,
  pollMs = 2000,
): Promise<SyncResult> {
  const first = (await start()) as JobView | SyncResult;
  // Older agents (or ?wait=true) answer with the result itself.
  if (!('jobId' in first)) return first as SyncResult;

  let job = first as JobView;
  onProgress(job);
  let misses = 0;
  while (job.status === 'running') {
    await sleep(pollMs);
    try {
      job = (await qboApi.getJob(job.jobId)) as JobView;
      misses = 0;
      onProgress(job);
    } catch (e) {
      // A dropped poll is not a failed job; give the tunnel a few tries.
      if (++misses > 5) throw e;
    }
  }
  if (job.status === 'done' && job.result) return job.result;
  throw new Error(job.error || `job ${job.status}`);
}
